// Matching flexível de número de pedido para o "Aceite de Responsabilidade"
// (Conferência de Carga) — fev/2026.
//
// Contexto: código de barras impresso no romaneio muitas vezes tem sufixos
// (ex: 4 zeros de padding, checksum, código da loja) que não fazem parte do
// "número do pedido" que o atendente digita no sistema. O entregador escaneia
// "2608007090000" mas o atendente lançou "260800709" — precisamos casar
// mesmo assim.
//
// Regras:
//   1. Igualdade exata (após remover não-dígitos)
//   2. `query.startsWith(pedido)` — scanner leu MAIS que o cadastrado
//   3. `pedido.startsWith(query)` — atendente digitou parcial
//   4. `query.includes(pedido)` — pedido é substring do escaneado
//   5. `pedido.includes(query)` — query digitada é substring do pedido
//   Para regras 4/5 exigimos query com >= 4 dígitos para evitar falso-positivo.
//
// fev/2026 (v6): Aceite por-pedido — quando uma entrega tem múltiplos pedidos
// preenchidos (receita + revenda, ou array pedidos_multiplos), o entregador
// precisa escanear TODOS antes da entrega transicionar pra em_transito.
// classifyScan agora retorna `matched_pedido` (o número específico que casou)
// e adiciona kind `pedido_ja_escaneado` quando o pedido já está em
// `entrega.pedidos_confirmados`.

export function normalizeDigits(s) {
  return String(s || "").replace(/\D/g, "");
}

export function collectPedidos(entrega) {
  const arr = [];
  if (entrega?.numero_pedido) arr.push(entrega.numero_pedido);
  if (entrega?.numero_pedido_receita) arr.push(entrega.numero_pedido_receita);
  if (entrega?.numero_pedido_revenda) arr.push(entrega.numero_pedido_revenda);
  if (Array.isArray(entrega?.pedidos_multiplos)) {
    entrega.pedidos_multiplos.forEach((p) => p && arr.push(p));
  }
  return arr;
}

// Coleta todos os pedidos preenchidos da entrega — normalizados (só dígitos) e
// deduplicados. Espelha _expected_pedidos_from_entrega() do backend.
export function expectedPedidos(entrega) {
  const seen = new Set();
  const out = [];
  collectPedidos(entrega).forEach((p) => {
    const n = normalizeDigits(p);
    if (n && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  });
  return out;
}

export function confirmedPedidos(entrega) {
  return (entrega?.pedidos_confirmados || [])
    .map(normalizeDigits)
    .filter(Boolean);
}

// Retorna { pedidos_pendentes, pedidos_confirmados, total, ratio }.
// Usado pela UI pra desenhar o badge "2/3 pedidos escaneados".
export function pedidoProgress(entrega) {
  const expected = expectedPedidos(entrega);
  const confirmed = confirmedPedidos(entrega);
  const confirmedSet = new Set(confirmed);
  const pendentes = expected.filter((p) => !confirmedSet.has(p));
  return {
    expected,
    confirmed: expected.filter((p) => confirmedSet.has(p)),
    pendentes,
    total: expected.length,
    done: expected.length - pendentes.length,
    all_done: pendentes.length === 0,
  };
}

// Retorna { score:0..100, pedido: <número que casou> } ou null.
// Score alto = confiança maior no match.
function scorePedidoMatch(q, entrega) {
  const peds = collectPedidos(entrega).map(normalizeDigits).filter(Boolean);
  let best = null;
  for (const pd of peds) {
    let s = 0;
    if (pd === q) s = 100;
    else if (q.startsWith(pd) && pd.length >= 4) s = 85;
    else if (pd.startsWith(q) && q.length >= 4) s = 75;
    else if (q.includes(pd) && pd.length >= 4) s = 65;
    else if (pd.includes(q) && q.length >= 4) s = 55;
    if (s > 0 && (!best || s > best.score)) best = { score: s, pedido: pd };
  }
  return best;
}

// Classifica o resultado de um "scan" ou "busca" contra a lista de entregas
// atribuídas ao entregador logado. Preferência:
//   - match em aguardando_conferencia (o único que faz sentido "confirmar")
//   - se o pedido específico já está em pedidos_confirmados → pedido_ja_escaneado
//   - se estiver em outro status, informa que já foi confirmado (entrega toda)
//   - senão, "não atribuído a você"
//
// v6 (fev/2026): retorna `matched_pedido` (número específico que casou), útil
// pro caller enviar ao backend só o pedido escaneado, não a entrega inteira.
export function classifyScan(query, items) {
  const q = normalizeDigits(query);
  if (!q) return { kind: "empty" };
  const list = Array.isArray(items) ? items : [];

  const rank = (it) => {
    const s = scorePedidoMatch(q, it);
    return s ? { entrega: it, ...s } : null;
  };

  const conferencia = list.filter((i) => i.status === "aguardando_conferencia");
  const outros = list.filter((i) => i.status !== "aguardando_conferencia");

  const bestConf = conferencia.map(rank).filter(Boolean).sort((a, b) => b.score - a.score)[0];
  if (bestConf) {
    const confirmed = new Set(confirmedPedidos(bestConf.entrega));
    if (confirmed.has(bestConf.pedido)) {
      return {
        kind: "pedido_ja_escaneado",
        entrega: bestConf.entrega,
        pedido: bestConf.pedido,
        matched_pedido: bestConf.pedido,
      };
    }
    return {
      kind: "match",
      entrega: bestConf.entrega,
      pedido: bestConf.pedido,
      matched_pedido: bestConf.pedido,
      score: bestConf.score,
    };
  }

  const bestOutro = outros.map(rank).filter(Boolean).sort((a, b) => b.score - a.score)[0];
  if (bestOutro) return { kind: "already_confirmed", entrega: bestOutro.entrega, pedido: bestOutro.pedido };

  return { kind: "not_in_list" };
}
